import { createTheme } from '@mui/material/styles';

// Modern color palette for Mover Portal
const palette = {
  primary: {
    main: '#008D7B',
    light: '#E1F7F3',
    dark: '#007566',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#173057',
    light: '#E8EFF7',
    dark: '#102449',
    contrastText: '#ffffff',
  },
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
  },
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
  },
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
  },
  background: {
    default: '#F7FAFC',
    paper: '#ffffff',
    dashboard: '#F8FAFC',
  },
  text: {
    primary: '#173057',
    secondary: '#60748D',
    disabled: '#bdbdbd',
  },
};

// Dark theme palette
const darkPalette = {
  ...palette,
  mode: 'dark',
  primary: {
    main: '#90caf9',
    light: '#e3f2fd',
    dark: '#42a5f5',
    contrastText: '#000000',
  },
  background: {
    default: '#121212',
    paper: '#1e1e1e',
    dashboard: '#181818',
  },
  text: {
    primary: '#ffffff',
    secondary: '#b0b0b0',
    disabled: '#707070',
  },
};

// Typography settings
const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.4,
  },
};

// Component overrides
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: 6,
        fontWeight: 700,
        padding: '7px 14px',
        boxShadow: 'none',
      },
      contained: {
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        boxShadow: 'none',
        border: '1px solid #E4ECF2',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
      elevation1: {
        boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: '#ffffff',
        color: '#212121',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        borderBottom: '1px solid #e0e0e0',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: '1px solid #e0e0e0',
        boxShadow: 'none',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        fontWeight: 500,
      },
    },
  },
  MuiFab: {
    styleOverrides: {
      root: {
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
        },
      },
    },
  },
};

// Dark theme components
const darkComponents = {
  ...components,
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: '#1e1e1e',
        color: '#ffffff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        borderBottom: '1px solid #333333',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: '#1e1e1e',
        borderRight: '1px solid #333333',
        boxShadow: 'none',
      },
    },
  },
};

// Breakpoints
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
};

// Spacing
const spacing = 8;

// Create light theme
export const lightTheme = createTheme({
  palette,
  typography,
  components,
  breakpoints,
  spacing,
  shape: {
    borderRadius: 8,
  },
});

// Create dark theme
export const darkTheme = createTheme({
  palette: darkPalette,
  typography,
  components: darkComponents,
  breakpoints,
  spacing,
  shape: {
    borderRadius: 8,
  },
});

// Theme context values
export const themeSettings = {
  light: lightTheme,
  dark: darkTheme,
};

export default lightTheme;